/**
 * Theme Store
 *
 * Zustand store for managing application theme.
 * Supports light and dark modes with system preference detection.
 * Persists state to localStorage for consistent user experience.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'ketrics-tenant-theme';

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: getInitialTheme(),
      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleTheme: () => {
        const next = get().theme === 'light' ? 'dark' : 'light';
        set({ theme: next });
        applyTheme(next);
      },
    }),
    {
      name: STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

/**
 * Detect initial theme from localStorage or system preference
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { state } = JSON.parse(stored);
      if (state?.theme === 'light' || state?.theme === 'dark') {
        return state.theme;
      }
    }
    // Also check old storage key for migration
    const oldStored = localStorage.getItem('theme-storage');
    if (oldStored) {
      const { state } = JSON.parse(oldStored);
      if (state?.theme === 'dark') return 'dark';
      // 'default' and 'light' both map to 'light'
      return 'light';
    }
  } catch {
    // ignore parse errors
  }

  // Fall back to system preference
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

/**
 * Apply theme to the document root element
 */
function applyTheme(theme: Theme) {
  const root = document.documentElement;

  // Remove all theme classes (including legacy 'default')
  root.classList.remove('light', 'dark', 'default');

  // Add the dark class for Tailwind dark mode
  if (theme === 'dark') {
    root.classList.add('dark');
  }
}

/**
 * Initialize theme on app startup (before React renders)
 */
export function initializeTheme() {
  const theme = getInitialTheme();
  applyTheme(theme);
}
